'use client';

/**
 * BackgroundLayer - Unified background rendering
 * 
 * Handles all background types: color, image, video, gradient
 * with overlay support and responsive variants.
 */

import React, { useMemo } from 'react';
import { BackgroundConfig } from '../types';
import { BackgroundVideo } from '@/components/shared/BackgroundVideo';
import { isVideoSource } from '@/lib/video-utils';
import { getBrandColor } from '@/config/brand-colors';

interface BackgroundLayerProps {
  config?: BackgroundConfig;
  className?: string;
  priority?: boolean;
}

export function BackgroundLayer({ config, className = '', priority = false }: BackgroundLayerProps) {
  if (!config || config.type === 'none') {
    return null;
  }

  const isVideo = useMemo(() => {
    if (config.type === 'video') return true;
    if (config.type === 'image' && config.value) {
      return isVideoSource(config.value);
    }
    return false;
  }, [config.type, config.value]);

  const overlayStyle = useMemo(() => {
    if (!config.overlay && config.overlayOpacity === undefined) return null;
    const opacity = config.overlayOpacity ?? 0.3;
    const color = config.overlay || 'black';
    return {
      backgroundColor: color === 'black' ? `rgba(0,0,0,${opacity})` : 
                       color === 'white' ? `rgba(255,255,255,${opacity})` :
                       color.startsWith('#') ? `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}` :
                       color,
    };
  }, [config.overlay, config.overlayOpacity]);

  // Color background
  if (config.type === 'color' && config.value) {
    const bgColor = config.value.startsWith('#') || config.value.startsWith('rgb') 
      ? config.value 
      : getBrandColor(config.value as 'deepNavy' | 'primaryBlue' | 'accentOrange' | 'greenAccent' | 'white' | 'black') || config.value;
    
    return (
      <div 
        className={`absolute inset-0 z-0 ${className}`}
        style={{ backgroundColor: bgColor }}
      />
    );
  }

  // Gradient background
  if (config.type === 'gradient' && config.value) {
    return (
      <div 
        className={`absolute inset-0 z-0 ${className}`}
        style={{ background: config.value }}
      />
    );
  }

  // Video background
  if (isVideo && config.value) {
    // Build responsive video URL object if mobile variant exists
    const videoUrl = config.mobileValue 
      ? { desktop: config.value, mobile: config.mobileValue }
      : config.value;
    
    return (
      <div className={`absolute inset-0 z-0 ${className}`}>
        <BackgroundVideo
          videoUrl={videoUrl}
          className="w-full h-full"
          gradientOverlayClassName={overlayStyle ? undefined : "absolute inset-0 bg-black/30 z-10 pointer-events-none"}
        />
        {overlayStyle && (
          <div className="absolute inset-0 z-10 pointer-events-none" style={overlayStyle} />
        )}
      </div>
    );
  }

  // Image background
  if (config.type === 'image' && config.value) {
    return (
      <div className={`absolute inset-0 z-0 ${className}`}>
        {/* Desktop image */}
        <img
          src={config.value}
          alt=""
          className={`w-full h-full object-cover ${config.mobileValue ? 'hidden md:block' : ''}`}
          style={{
            filter: config.blur ? `blur(${config.blur}px)` : undefined,
          }}
        />
        {/* Mobile image (if different) */}
        {config.mobileValue && (
          <img
            src={config.mobileValue}
            alt=""
            className="w-full h-full object-cover block md:hidden"
            style={{
              filter: config.blur ? `blur(${config.blur}px)` : undefined,
            }}
          />
        )}
        {/* Overlay */}
        {overlayStyle && (
          <div className="absolute inset-0 z-10 pointer-events-none" style={overlayStyle} />
        )}
      </div>
    );
  }

  return null;
}

export default BackgroundLayer;
